import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { Btn } from '../../components/Btn';
import { Card } from '../../components/Card';
import { KpiCard } from '../../components/KpiCard';
import { PageHeader } from '../../components/PageHeader';
import { dashboardApi } from '../../lib/apiClient';
import { fmtAmount, fmtInt } from '../../lib/format';
import { useBranchScope } from '../../lib/useBranchScope';

const today = () => new Date().toISOString().slice(0, 10);

export function ZReportView() {
  const { scope, ready } = useBranchScope();
  const [date, setDate] = useState(today);
  const [downloading, setDownloading] = useState<'pdf' | 'excel' | null>(null);
  const branchId = scope.branchId ?? undefined;

  const { data, isLoading } = useQuery({
    queryKey: ['report', 'z-report', date, branchId, scope.allowedBranchIds],
    queryFn: () => dashboardApi.getZReport({ date, branchId }),
    enabled: ready,
  });

  const download = async (format: 'pdf' | 'excel') => {
    setDownloading(format);
    try {
      const blob = await dashboardApi.downloadZReport({
        date,
        branchId,
        format,
      });
      downloadBlob(
        blob,
        `z-report-${date}.${format === 'pdf' ? 'pdf' : 'csv'}`,
      );
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Z-Report"
        subtitle="รายงานปิดวัน"
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <input
              aria-label="วันที่"
              className="h-10 rounded-btn border border-line bg-surface px-3 text-body text-ink"
              type="date"
              value={date}
              onChange={(event) => setDate(event.target.value)}
            />
            <Btn
              kind="default"
              onClick={() => void download('pdf')}
              disabled={!data || downloading !== null}
            >
              PDF
            </Btn>
            <Btn
              kind="default"
              onClick={() => void download('excel')}
              disabled={!data || downloading !== null}
            >
              Excel
            </Btn>
          </div>
        }
      />

      {(data?.sales.legacyAccountingReceiptCount ?? 0) > 0 && (
        <p className="rounded-btn bg-warn-light px-3 py-2 text-sm text-warn">
          มีข้อมูลเก่า {fmtInt(data!.sales.legacyAccountingReceiptCount)}{' '}
          บิลที่ยังไม่มีการแยกยอดก่อนส่วนลด ระบบใช้ยอดสุทธิของบิลเหล่านั้น
          และไม่รวมโปรโมชั่นเดิมซ้ำ
        </p>
      )}

      <div className="grid gap-3 md:grid-cols-3 xl:grid-cols-4">
        <KpiCard
          label="ยอดก่อนส่วนลด"
          value={data ? fmtAmount(data.sales.gross ?? data.sales.total) : '-'}
          sub={data?.businessDate}
        />
        <KpiCard
          label="ยอดขายสุทธิ"
          value={data ? fmtAmount(data.sales.total) : '-'}
        />
        <KpiCard
          label="มูลค่าโปรโมชั่น"
          value={data ? fmtAmount(data.sales.promotionDiscount ?? 0) : '-'}
          tone="warn"
        />
        <KpiCard
          label="ส่วนลดท้ายบิล"
          value={data ? fmtAmount(data.sales.billDiscount ?? 0) : '-'}
          tone="warn"
        />
        <KpiCard
          label="ยอดปัดเศษ"
          value={data ? fmtAmount(data.sales.roundingDelta ?? 0) : '-'}
        />
        <KpiCard
          label="กำไรขั้นต้น"
          value={data ? fmtAmount(data.sales.grossProfit) : '-'}
          tone="ok"
        />
        <KpiCard
          label="เงินสดต่าง"
          value={data ? fmtAmount(data.cash.discrepancy) : '-'}
          tone={data && data.cash.discrepancy !== 0 ? 'warn' : 'ok'}
        />
        <KpiCard
          label="VAT"
          value={data ? fmtAmount(data.vat.vatAmount) : '-'}
          sub="7%"
        />
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <Card title="โปรโมชั่นที่ใช้">
          <Rows
            empty={isLoading ? 'กำลังโหลด' : 'ไม่มีโปรโมชั่น'}
            rows={(data?.promotions ?? []).map((row) => ({
              key: row.promotionId,
              label: `${row.promotionName} · ${fmtInt(row.receiptCount)} บิล${
                row.freeQuantity > 0
                  ? ` · แถม ${fmtInt(row.freeQuantity)} ชิ้น`
                  : ''
              }`,
              value: fmtAmount(row.discountAmount),
            }))}
          />
        </Card>
        <Card title="ช่องทางชำระเงิน">
          <Rows
            empty={isLoading ? 'กำลังโหลด' : 'ไม่มีข้อมูล'}
            rows={(data?.payments ?? []).map((row) => ({
              key: row.method,
              label: row.label,
              value: fmtAmount(row.amount),
            }))}
          />
        </Card>
        <Card title="หมวดหมู่">
          <Rows
            empty={isLoading ? 'กำลังโหลด' : 'ไม่มีข้อมูล'}
            rows={(data?.categories ?? []).map((row) => ({
              key: row.categoryId,
              label: row.categoryName,
              value: fmtAmount(row.amount),
            }))}
          />
        </Card>
        <Card title="สินค้าขายดี">
          <Rows
            empty={isLoading ? 'กำลังโหลด' : 'ไม่มีข้อมูล'}
            rows={(data?.topByQty ?? []).map((row) => ({
              key: row.productId,
              label: row.productName,
              value: `${fmtInt(row.quantity)} / ${fmtAmount(row.amount)}`,
            }))}
          />
        </Card>
      </div>
    </div>
  );
}

function Rows({
  rows,
  empty,
}: {
  rows: Array<{ key: string; label: string; value: string }>;
  empty: string;
}) {
  if (rows.length === 0) {
    return <div className="py-8 text-center text-body text-ink-3">{empty}</div>;
  }
  return (
    <div className="divide-y divide-line">
      {rows.map((row) => (
        <div
          key={row.key}
          className="flex items-center justify-between gap-4 py-3"
        >
          <span className="min-w-0 truncate text-body font-medium text-ink">
            {row.label}
          </span>
          <span className="num shrink-0 text-body font-bold text-ink">
            {row.value}
          </span>
        </div>
      ))}
    </div>
  );
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
